import { LoanStatus } from "web_modules/shared/loan_statuses";
import { DECISION as LoanDecision } from "web_modules/shared/loan_decisions";
import { isProduction, isDevelopment } from "config";

import {
  colsForInitialBorrowerUnderwrite,
  colsForTasksToComplete,
  colsForConditionsReadyToReview,
  colsForBorrowerMessage,
  colsForCreditReportPullIssues,
  colsForFICODeclines,
  colsSendForNoticeOfIncompleteApplication,
  colsForSecondReviewForMortgageDenial,
  colsRESPAForTasksToComplete,
  colsRESPACompleteConditionsReadyToReview,
  colsRESPACompleteBorrowerMessage,
  colsRESPACompleteInitialBorrowerUnderwrites,
  colsRESPACompleteSecondReviewForMortgageDenial,
  colsRESPAUWAppraisalReview,
} from "./ColumnData";

import { Queue, TaskingAppShape } from "./types";
import {
  QueueRole,
  QueueCategory,
  QueueColumnNames as Cols,
  filterRESPACompleteApps,
  filterPreApps,
  sortOldestFirst,
} from "./shared";

const shouldBeFilteredFromAppraisalQueue = (app: TaskingAppShape): boolean => {
  if (isProduction()) {
    const appIDs = [
      28,
      62,
      61,
      121,
      134,
      106,
      213,
      161,
      273,
      266,
      251,
      308,
      352,
      372,
      371,
      248,
      347,
      455,
      435,
      227,
      471,
      484,
      451,
      238,
      551,
      554,
      731,
      658,
      763,
      728,
      699,
      684,
      680,
      828,
      862,
      750,
      1031,
      638,
      1103,
      782,
      1122,
      777,
      1143,
    ];
    return appIDs.indexOf(app.loan_application_id) >= 0;
  }
  return false;
};
// Destructure some things for cleanliness
const { PreApplications, RESPAComplete } = QueueCategory;
const { Underwriter } = QueueRole;

/* ********************* */
/* PreApplication Queues */
/* ********************* */
const initialBorrowerUnderwrite: Queue = {
  name: "Initial Borrower Underwrites",
  category: PreApplications,
  columns: colsForInitialBorrowerUnderwrite,
  highlightedColumns: [Cols.OutstandingDocuments, Cols.PreAppUWDue],
  role: Underwriter,
  openToPath: "UnderwritingSummaryHurdle",
  datePropertyName: Cols.PreAppUWDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps).filter(
      a =>
        a[Cols.LoanStatus] === LoanStatus.AuthPackageSigned &&
        a[Cols.AssignedUnderwriterName] === null,
    );
    return sortOldestFirst(queue, Cols.PreAppUWDue);
  },
};

const tasksToComplete: Queue = {
  name: "Tasks to Complete ",
  category: PreApplications,
  columns: colsForTasksToComplete,
  highlightedColumns: [Cols.LoanDecisionDate],
  role: Underwriter,
  openToPath: "TasksHurdle",
  datePropertyName: Cols.UWTaskFirstDueBy,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps).filter(a => a[Cols.UWTaskCount] > 0);
    return sortOldestFirst(queue, Cols.UWTaskFirstDueBy);
  },
};

const conditionsReadyToReview: Queue = {
  name: "Conditions Ready to Review",
  category: PreApplications,
  columns: colsForConditionsReadyToReview,
  highlightedColumns: [
    Cols.ConditionsPreReadyToReview,
    Cols.ConditionsReadyToReview,
    Cols.ConditionResponseDue,
  ],
  role: Underwriter,
  openToPath: "ConditionsHurdle",
  datePropertyName: Cols.ConditionResponseDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps).filter(a => a[Cols.ConditionsReadyToReview] !== null);
    return sortOldestFirst(queue, Cols.ConditionResponseDue);
  },
};

const borrowerMessage: Queue = {
  name: "Borrower Message",
  category: PreApplications,
  columns: colsForBorrowerMessage,
  highlightedColumns: [Cols.MessagesToReview, Cols.MessageResponseDue],
  role: Underwriter,
  openToPath: "MessagesHurdle",
  datePropertyName: Cols.MessageResponseDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps).filter(a => a[Cols.MessagesToReview] !== null);
    return sortOldestFirst(queue, Cols.MessageResponseDue);
  },
};

const secondReviewForMortgageDenial: Queue = {
  name: "Second Review For Mortgage Denial",
  category: PreApplications,
  columns: colsForSecondReviewForMortgageDenial,
  highlightedColumns: [Cols.PreAppAANDue],
  role: Underwriter,
  openToPath: "UnderwritingSummaryHurdle",
  datePropertyName: Cols.PreAppAANDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps)
      .filter(
        a =>
          a[Cols.LoanStatus] === LoanStatus.InBorrowerUnderwriting &&
          a[Cols.LoanDecision] === LoanDecision.DENIAL_RECOMMENDED,
      )
      .map(a => {
        return {
          ...a,
          [Cols.PreAppAANReason]: "See Loan Decision Details",
        };
      });

    return sortOldestFirst(queue, Cols.PreAppAANDue);
  },
};

const creditReportPullIssues: Queue = {
  name: "Credit Report Pull Issues",
  category: PreApplications,
  columns: colsForCreditReportPullIssues,
  highlightedColumns: [Cols.LoanStatusDate],
  role: Underwriter,
  openToPath: "notes",
  datePropertyName: Cols.LoanStatusDate,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps).filter(
      a => a[Cols.LoanStatus] === LoanStatus.CreditReportError,
    );
    return sortOldestFirst(queue, Cols.LoanStatusDate);
  },
};

const FICODeclines: Queue = {
  name: "AANs for FICO Declines",
  category: PreApplications,
  columns: colsForFICODeclines,
  highlightedColumns: [Cols.PreAppAANDue],
  role: Underwriter,
  openToPath: "DocumentsHurdle",
  datePropertyName: Cols.PreAppAANDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps)
      .filter(
        a =>
          a[Cols.LoanStatus] === LoanStatus.CreditReportReceived &&
          a[Cols.LoanDecision] === LoanDecision.DENIAL_RECOMMENDED,
      )
      .map(a => {
        return {
          ...a,
          [Cols.PreAppAANReason]: "FICO",
        };
      });

    return sortOldestFirst(queue, Cols.PreAppAANDue);
  },
};

const sendNoticeOfIncompleteApplication: Queue = {
  name: "Send Notice of Incomplete Application (NOIA)",
  category: PreApplications,
  columns: colsSendForNoticeOfIncompleteApplication,
  highlightedColumns: [Cols.NOIADue],
  role: Underwriter,
  openToPath: "UnderwritingSummaryHurdle",
  datePropertyName: Cols.NOIADue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterPreApps(apps).filter(
      a => a[Cols.LoanStatus] === LoanStatus.CreditReportReceived && a[Cols.LoanDecision] === null,
    );

    return sortOldestFirst(queue, Cols.NOIADue);
  },
};

/* ******************** */
// RESPA Complete Queues
/* ******************** */
const RESPAInitialRESPAUnderwrites: Queue = {
  name: "Initial RESPA Underwrites",
  category: RESPAComplete,
  columns: colsRESPACompleteInitialBorrowerUnderwrites,
  highlightedColumns: [Cols.ClosingDate],
  role: Underwriter,
  openToPath: "UnderwritingSummaryHurdle",
  datePropertyName: Cols.LoanStatusDate,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterRESPACompleteApps(apps).filter(
      a =>
        a[Cols.LoanStatus] === LoanStatus.ApplicationComplete &&
        a[Cols.LoanDecision] !== LoanDecision.WITHDRAWN &&
        a[Cols.LoanDecision] !== LoanDecision.DENIAL_RECOMMENDED &&
        a[Cols.LoanDecision] !== LoanDecision.MORTGAGE_DENIED,
    );

    // return sortOldestFirst(queue, Cols.LoanStatusDate);
    return sortOldestFirst(queue, Cols.ClosingDate);
  },
};

const RESPAtasksToComplete: Queue = {
  name: "Tasks to Complete",
  category: RESPAComplete,
  columns: colsRESPAForTasksToComplete,
  highlightedColumns: [Cols.LoanDecisionDate],
  role: Underwriter,
  openToPath: "TasksHurdle",
  datePropertyName: Cols.UWTaskFirstDueBy,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterRESPACompleteApps(apps).filter(a => a[Cols.UWTaskCount] > 0);
    // return sortOldestFirst(queue, Cols.UWTaskFirstDueBy);
    return sortOldestFirst(queue, Cols.ClosingDate);
  },
};

const RESPAconditionsReadyToReview: Queue = {
  name: "Conditions Ready to Review ",
  category: RESPAComplete,
  columns: colsRESPACompleteConditionsReadyToReview,
  highlightedColumns: [
    Cols.ConditionsPreReadyToReview,
    Cols.ConditionsReadyToReview,
    Cols.ConditionResponseDue,
  ],
  role: Underwriter,
  openToPath: "ConditionsHurdle",
  datePropertyName: Cols.ConditionResponseDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterRESPACompleteApps(apps).filter(
      a => a[Cols.ConditionsReadyToReview] !== null,
    );
    // return sortOldestFirst(queue, Cols.ConditionResponseDue);
    return sortOldestFirst(queue, Cols.ClosingDate);
  },
};

const RESPABorrowerMessage: Queue = {
  name: "Borrower Message ",
  category: RESPAComplete,
  columns: colsRESPACompleteBorrowerMessage,
  highlightedColumns: [Cols.MessagesToReview, Cols.MessageResponseDue],
  role: Underwriter,
  openToPath: "MessagesHurdle",
  datePropertyName: Cols.MessageResponseDue,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterRESPACompleteApps(apps).filter(a => a[Cols.MessagesToReview] !== null);

    // return sortOldestFirst(queue, Cols.MessageResponseDue);
    return sortOldestFirst(queue, Cols.ClosingDate);
  },
};

const RESPASecondReviewForMortgageDenial: Queue = {
  name: "Second Review for Mortgage Denial ",
  category: RESPAComplete,
  columns: colsRESPACompleteSecondReviewForMortgageDenial,
  highlightedColumns: [Cols.RegBCreditDecisionDue],
  role: Underwriter,
  openToPath: "UnderwritingSummaryHurdle",
  datePropertyName: Cols.LoanDecisionDate,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterRESPACompleteApps(apps).filter(
      a => a[Cols.LoanDecision] === LoanDecision.DENIAL_RECOMMENDED,
    );

    // return sortOldestFirst(queue, Cols.LoanDecisionDate);
    return sortOldestFirst(queue, Cols.ClosingDate);
  },
};

const RESPAUWAppraisalReview: Queue = {
  name: "UW Appraisal Review",
  category: RESPAComplete,
  columns: colsRESPAUWAppraisalReview,
  highlightedColumns: [Cols.AppraisalReceivedDate],
  role: Underwriter,
  openToPath: "AppraisalReportHurdle",
  datePropertyName: Cols.AppraisalReceivedDate,
  recipe: (apps: TaskingAppShape[]): TaskingAppShape[] => {
    const queue = filterRESPACompleteApps(apps).filter(
      a =>
        !shouldBeFilteredFromAppraisalQueue(a) &&
        a[Cols.AppraisalReceivedDate] !== null &&
        a[Cols.AppraisalApprovedDate] === null &&
        a[Cols.AppraisalSentDate] === null,
    );

    // return sortOldestFirst(queue, Cols.AppraisalReceivedDate);
    return sortOldestFirst(queue, Cols.ClosingDate);
  },
};

// Queues that are released
const queuesForProduction = [
  // RESPA
  RESPAInitialRESPAUnderwrites,
  RESPAtasksToComplete,
  RESPAconditionsReadyToReview,
  RESPABorrowerMessage,
  RESPASecondReviewForMortgageDenial,
  RESPAUWAppraisalReview,

  // PreApp
  creditReportPullIssues,
  initialBorrowerUnderwrite,
  tasksToComplete,
  conditionsReadyToReview,
  borrowerMessage,
  secondReviewForMortgageDenial,
  FICODeclines,
  sendNoticeOfIncompleteApplication,
];

// FIXME: Later, we should NOT use variables like this, but it's convenient for local development.
// Queues that are done but yet-to-be-presented
const queuesToBeShownToOps = [...queuesForProduction];

const queuesInDevelopment = [...queuesToBeShownToOps];

// FIXME: Export in array for easy iteration
const UWQueuesData: Queue[] = isProduction()
  ? queuesForProduction
  : isDevelopment()
  ? queuesInDevelopment
  : queuesToBeShownToOps;

export default UWQueuesData;
